import React, { useState, useEffect } from 'react';
import { X, Package, Search, Plus, ShoppingCart, Trash, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { getConsumables } from '@/pages/parametrizacion/consumables/Services/consumables.services';
import { getEdificioInsumos } from '@/services/edificioInsumos.service';
import { fetchEdificios } from '@/pages/parametrizacion/edificios/Services/edificios.services';

const SelectInsumoModal = ({ isOpen, onClose, edificioId: propEdificioId, onSelect, onSave, showSaveButton = false }) => {
  const { toast } = useToast();
  const [insumos, setInsumos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  const [observacionGeneral, setObservacionGeneral] = useState('');
  const [edificios, setEdificios] = useState([]);
  const [selectedEdificio, setSelectedEdificio] = useState(null);
  const [isLoadingEdificios, setIsLoadingEdificios] = useState(false);
  const [accumulatedItems, setAccumulatedItems] = useState([]); // Items acumulados para guardar
  
  // Fecha de entrega (por defecto hoy)
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  const [fechaEntrega, setFechaEntrega] = useState(getTodayDate());

  // Si no se pasa edificioId, usar el edificio seleccionado internamente
  const edificioId = propEdificioId || selectedEdificio?._id;

  useEffect(() => {
    if (isOpen && !propEdificioId) {
      loadEdificios();
    }
  }, [isOpen, propEdificioId]);

  useEffect(() => {
    if (isOpen && edificioId) {
      loadInsumos();
    }
  }, [isOpen, edificioId]);

  const loadInsumos = async () => {
    setIsLoading(true);
    try {
      // Obtener IDs de insumos asignados al edificio
      const assignedResponse = await getEdificioInsumos(edificioId);
      const assignedIds = assignedResponse.data || [];

      if (assignedIds.length === 0) {
        toast({
          title: "Sin insumos",
          description: "Este portafolio no tiene insumos asignados",
          variant: "destructive",
        });
        setInsumos([]);
        setIsLoading(false);
        return;
      }

      // Obtener todos los insumos
      const allInsumosResponse = await getConsumables({
        limit: 1000,
        offset: 1,
        query: { estado: 'Activo' }
      });

      // Filtrar solo los asignados al edificio
      const filteredInsumos = (allInsumosResponse.data || []).filter(insumo =>
        assignedIds.includes(insumo._id)
      );

      setInsumos(filteredInsumos);
    } catch (error) {
      console.error('Error al cargar insumos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los insumos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadEdificios = async () => {
    setIsLoadingEdificios(true);
    try {
      const response = await fetchEdificios({
        limit: 1000,
        offset: 1,
        query: { estado: 'Activo' }
      });
      setEdificios(response.data || []);
    } catch (error) {
      console.error('Error al cargar edificios:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los portafolios",
        variant: "destructive",
      });
    } finally {
      setIsLoadingEdificios(false);
    }
  };

  const handleToggleItem = (insumo) => {
    setSelectedItems(prev => {
      const newSelected = { ...prev };
      if (newSelected[insumo._id]) {
        delete newSelected[insumo._id];
      } else {
        newSelected[insumo._id] = {
          insumo,
          cantidad: 1
        };
      }
      return newSelected;
    });
  };

  const handleCantidadChange = (insumoId, cantidad) => {
    setSelectedItems(prev => ({
      ...prev,
      [insumoId]: {
        ...prev[insumoId],
        cantidad: parseInt(cantidad) || 1
      }
    }));
  };

  const handleAgregarTodos = () => {
    const selectedCount = Object.keys(selectedItems).length;
    
    if (selectedCount === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un insumo",
        variant: "destructive",
      });
      return;
    }

    // Validar cantidades
    const invalidItems = Object.values(selectedItems).filter(item => item.cantidad <= 0);
    if (invalidItems.length > 0) {
      toast({
        title: "Error",
        description: "Todas las cantidades deben ser mayores a 0",
        variant: "destructive",
      });
      return;
    }

    // Crear array de items seleccionados
    const itemsToAdd = Object.values(selectedItems).map(item => ({
      id: Date.now() + Math.random(),
      insumoId: item.insumo._id,
      nombre: item.insumo.nombre,
      tipo: item.insumo.tipo,
      cantidad: item.cantidad,
      observacion: observacionGeneral
    }));

    if (showSaveButton) {
      // Acumular items internamente para guardar después
      setAccumulatedItems(prev => [...prev, ...itemsToAdd]);
      
      // Limpiar selección actual para permitir agregar más
      setSelectedItems({});
      setObservacionGeneral('');
      
      toast({
        title: "Items agregados",
        description: `${itemsToAdd.length} item(s) agregado(s). Total: ${accumulatedItems.length + itemsToAdd.length}`,
      });
    } else {
      // Flujo original: llamar onSelect para cada item
      itemsToAdd.forEach(item => onSelect(item));
      
      // Reset form y cerrar
      setSelectedItems({});
      setObservacionGeneral('');
      onClose();
    }
  };

  const handleSave = () => {
    if (!propEdificioId && !selectedEdificio) {
      toast({
        title: "Error",
        description: "Debe seleccionar un portafolio",
        variant: "destructive",
      });
      return;
    }

    const selectedCount = Object.keys(selectedItems).length;
    
    // Si hay items seleccionados actualmente, agregarlos primero
    let finalItems = [...accumulatedItems];
    
    if (selectedCount > 0) {
      // Validar cantidades
      const invalidItems = Object.values(selectedItems).filter(item => item.cantidad <= 0);
      if (invalidItems.length > 0) {
        toast({
          title: "Error",
          description: "Todas las cantidades deben ser mayores a 0",
          variant: "destructive",
        });
        return;
      }

      // Agregar items seleccionados actuales
      const itemsToAdd = Object.values(selectedItems).map(item => ({
        id: Date.now() + Math.random(),
        insumoId: item.insumo._id,
        nombre: item.insumo.nombre,
        tipo: item.insumo.tipo,
        cantidad: item.cantidad,
        observacion: observacionGeneral
      }));
      
      finalItems = [...finalItems, ...itemsToAdd];
    }

    // Validar que haya al menos un item
    if (finalItems.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un item antes de guardar",
        variant: "destructive",
      });
      return;
    }

    if (onSave) {
      // Pasar el edificio, items y fecha de entrega
      const edificioData = propEdificioId ? null : selectedEdificio;
      onSave(edificioData, finalItems, fechaEntrega);

      // Reset form completo
      setSelectedItems({});
      setObservacionGeneral('');
      setSelectedEdificio(null);
      setAccumulatedItems([]);
      setFechaEntrega(getTodayDate());
      onClose();
    }
  };

  const handleRemoveItem = (insumoId) => {
    setSelectedItems(prev => {
      const newSelected = { ...prev };
      delete newSelected[insumoId];
      return newSelected;
    });
  };

  const filteredInsumos = insumos.filter(insumo =>
    insumo.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    insumo.tipo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  const selectedCount = Object.keys(selectedItems).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Package className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Seleccionar Insumos
              </h2>
              <p className="text-sm text-gray-500">
                {selectedCount > 0 ? `${selectedCount} seleccionado(s)` : 'Seleccione los insumos que necesita'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Main Content - Lista de insumos */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Selector de Portafolio (solo si no se pasa edificioId) */}
          {!propEdificioId && (
            <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Seleccione un Portafolio *
              </Label>
              {isLoadingEdificios ? (
                <p className="text-sm text-gray-500">Cargando portafolios...</p>
              ) : (
                <select
                  value={selectedEdificio?._id || ''}
                  onChange={(e) => {
                    const edificio = edificios.find(ed => ed._id === e.target.value);
                    setSelectedEdificio(edificio);
                    setSelectedItems({});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">-- Seleccione un portafolio --</option>
                  {edificios.map((edificio) => (
                    <option key={edificio._id} value={edificio._id}>
                      {edificio.nombre} - {edificio.ciudad}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar insumos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Cargando insumos...</p>
            </div>
          ) : filteredInsumos.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No se encontraron insumos parametrizados</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={selectedCount === filteredInsumos.length && filteredInsumos.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const allSelected = {};
                            filteredInsumos.forEach(insumo => {
                              allSelected[insumo._id] = { insumo, cantidad: 1 };
                            });
                            setSelectedItems(allSelected);
                          } else {
                            setSelectedItems({});
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Insumo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                      Cantidad
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInsumos.map((insumo) => {
                    const isSelected = !!selectedItems[insumo._id];
                    const cantidad = selectedItems[insumo._id]?.cantidad || 1;

                    return (
                      <tr 
                        key={insumo._id}
                        className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-purple-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleItem(insumo)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-100' : 'bg-gray-100'}`}>
                              <Package className={`h-4 w-4 ${isSelected ? 'text-purple-600' : 'text-gray-600'}`} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{insumo.nombre}</p>
                              {insumo.descripcion && (
                                <p className="text-xs text-gray-500">{insumo.descripcion}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {insumo.tipo && <Badge variant="outline">{insumo.tipo}</Badge>}
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            min="1"
                            value={cantidad}
                            onChange={(e) => handleCantidadChange(insumo._id, e.target.value)}
                            disabled={!isSelected}
                            className="w-full"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          </div>

          {/* Sidebar - Resumen de Solicitud */}
          <div className="w-96 border-l border-gray-200 bg-gray-50 overflow-y-auto">
            <div className="p-6 sticky top-0 bg-gray-50 z-10 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingCart className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Resumen de Solicitud</h3>
              </div>
              <p className="text-xs text-gray-500">Items seleccionados para agregar</p>
            </div>

            <div className="p-6">
              {/* Fecha de Entrega */}
              <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
                <Label htmlFor="fecha-entrega" className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-purple-600" />
                  Fecha de Entrega *
                </Label>
                <Input
                  id="fecha-entrega"
                  type="date"
                  value={fechaEntrega}
                  onChange={(e) => setFechaEntrega(e.target.value)}
                  className="w-full"
                />
              </div>

              {selectedCount === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 font-medium mb-1">
                    No hay insumos seleccionados
                  </p>
                  <p className="text-xs text-gray-400">
                    Seleccione insumos de la lista
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Lista de items seleccionados */}
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {Object.values(selectedItems).map((item) => (
                      <div key={item.insumo._id} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-medium text-sm text-gray-900">{item.insumo.nombre}</p>
                            {item.insumo.tipo && (
                              <p className="text-xs text-gray-500">
                                Tipo: {item.insumo.tipo}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveItem(item.insumo._id)}
                            className="h-6 w-6 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Eliminar"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Cantidad:</span>
                          <span className="font-semibold text-gray-900">{item.cantidad} unidades</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totales */}
                  <div className="border-t border-gray-300 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total de insumos:</span>
                      <span className="font-semibold text-gray-900">{selectedCount}</span>
                    </div>
                    <div className="flex justify-between text-base font-bold border-t border-gray-300 pt-2">
                      <span className="text-gray-900">Total de unidades:</span>
                      <span className="text-purple-600">
                        {Object.values(selectedItems).reduce((sum, item) => sum + item.cantidad, 0)}
                      </span>
                    </div>
                  </div>

                  {/* Observación General */}
                  <div className="space-y-2 border-t border-gray-300 pt-4">
                    <Label htmlFor="observacion-general" className="text-sm font-medium text-gray-700">
                      Observación General
                    </Label>
                    <Textarea
                      id="observacion-general"
                      value={observacionGeneral}
                      onChange={(e) => setObservacionGeneral(e.target.value)}
                      placeholder="Observación para todos los items..."
                      rows={3}
                      className="text-sm"
                    />
                  </div>

                  {/* Acumulados (si existen) */}
                  {showSaveButton && accumulatedItems.length > 0 && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <ShoppingCart className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-900">
                          Items acumulados
                        </span>
                      </div>
                      <p className="text-xs text-purple-700">
                        {accumulatedItems.length} item(s) listo(s) para guardar
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {showSaveButton && accumulatedItems.length > 0 ? (
              <span className="font-medium text-purple-600">
                {accumulatedItems.length} item(s) listo(s) para guardar
              </span>
            ) : selectedCount > 0 ? (
              <span className="font-medium">
                {selectedCount} insumo(s) seleccionado(s)
              </span>
            ) : null}
          </div>
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancelar
            </Button>
            {!showSaveButton ? (
              <Button
                onClick={handleAgregarTodos}
                disabled={selectedCount === 0 || isLoading}
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar {selectedCount > 0 ? `(${selectedCount})` : ''}
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                disabled={isLoading}
              >
                Guardar Solicitud
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectInsumoModal;
